import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GroupDetailTabs from '../components/GroupDetailTabs';
import { useModal } from '../hooks/useModal';
import GroupJoinModal from '../modals/GroupJoinModal';
import AuthRequiredModal from '../modals/AuthRequiredModal';
import { useGroupInfo } from '../hooks/queries/useGroupInfo';
import { useGroupMembers } from '../hooks/queries/useGroupMembers';
import ShareModal from '../modals/ShareModal';
import Header from '../components/Header';
import GroupSettingsModal from '../modals/GroupSettingsModal';
import { getAuthToken, getUserData } from '../lib/client';
import { useLeaveGroup } from '../hooks/mutations/useLeaveGroup';
import { icons } from '../assets/icons';
import { isMobile } from 'react-device-detect';
import { logActiveUser } from '../utils/loggers';
import { useProfile } from '../hooks/queries/useMe';
import { getDeviceOS } from '../utils/deviceInfo';

const GroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = getAuthToken();
  const isAuthenticated = !!token;
  const currentUser = getUserData();
  const { data: user, isLoading: userLoading, isError: userError } = useProfile(isAuthenticated);
  const { data: group, isLoading: groupLoading, isError: groupError } = useGroupInfo(id);
  const { data: members, isLoading: membersLoading, isError: membersError } = useGroupMembers(id);

  const deviceOs = getDeviceOS();

  const { state } = useLocation();
  const initialEnter = state?.initialEnter;
  const initialGroupName = state?.initialGroupName;
  const initialMemberCount = state?.initialMemberCount;
  
  const handleBackClick = () => {
    // 히스토리에서 이전 페이지가 groups 페이지인지 확인
    const fromGroups = state?.from === '/groups';
    const canGoBack = window.history.length > 1;
    
    if (fromGroups && canGoBack) {
      // 이전 페이지가 groups 페이지라면 뒤로가기
      navigate(-1);
    } else {
      // 그렇지 않다면 groups 페이지로 replace
      navigate('/groups', { replace: true });
    }
  };

  const isLoading = userLoading || groupLoading || membersLoading;
  const isError = userError || groupError || membersError;

  const { isOpen, showModal, closeModal} = useModal();
  const [modalShown, setModalShown] = useState(false);
  const { mutate: leaveGroup } = useLeaveGroup();
  
  if (!id) {
    navigate('/', { replace: true });
  }
  
  const unlinkedMembers = useMemo(() => {
    return members?.filter(member => member.user_id === null);
  }, [members]);

  const linkedMembers = useMemo(() => {
    return members?.filter(member => member.user_id !== null);
  }, [members]);

  const memberNameMap = useMemo(() => {
    const map = new Map<string, string>();
    members?.forEach(member => {
      map.set(member.id, member.user_name);
    });
    return map;
  }, [members]);

  const { currentUserMemberId, isMemberOfGroup } = useMemo(() => {
    if (!isAuthenticated) return { currentUserMemberId: null, isMemberOfGroup: false };
    const currentUserMemberId = members?.find(member => member.user_id === currentUser?.id)?.id || null;
    const isMemberOfGroup = isAuthenticated && !!currentUserMemberId;
    return { currentUserMemberId, isMemberOfGroup };
  }, [members, currentUser, isAuthenticated]);
  
  useEffect(() => {
    if(isLoading || isOpen || !group || !id || modalShown) return;
    
    if (!isAuthenticated) {
      setModalShown(true);
      showModal(
        <AuthRequiredModal 
          groupId={id}
          groupName={group.name}
          closeModal={closeModal}
        />, 
        false
      );
    }
    // isAuthenticated true
    else if (deviceOs === 'ios' && user?.ios_installed) {
      navigate('/mobile', { state: { goto: id }, replace: true });
    }
    else if (deviceOs === 'android' && user?.android_installed) {
      navigate('/mobile', { state: { goto: id }, replace: true });
    }
    else if (initialEnter) {
      setModalShown(true);
      handleOpenShareModal();
    }
    else if (!isMemberOfGroup) {
      setModalShown(true);
      showModal(
        <GroupJoinModal 
          groupId={id}
          groupName={group.name}
          linkedMembers={linkedMembers as any}
          unlinkedMembers={unlinkedMembers as any}
          currentUserId={currentUser?.id}
          currentUserName={currentUser?.name}
          closeModal={closeModal}
          hasPassword={group.hasPassword}
        />, 
        false
      );
    }
  }, [isAuthenticated, isMemberOfGroup, isLoading, group, isOpen, unlinkedMembers, id, currentUser, initialEnter, modalShown]);

  useEffect(() => {
    if (!isAuthenticated || !id || !isMemberOfGroup) {
      return;
    }
    //yyyy-mm-dd
    const YYYYMMDD = new Date().toLocaleDateString('ko-kr', {year:'numeric', month:'2-digit', day:'2-digit'}).replace(/\./g,'').replace(/ /g,'').replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
    const lastVisitDate = localStorage.getItem(`lastVisitDate-${id}`); // 2025-08-27
    if (lastVisitDate === YYYYMMDD) {
      return;
    }
    logActiveUser(id, YYYYMMDD, isMobile ? 'mobile-web' : 'desktop-web');
    localStorage.setItem(`lastVisitDate-${id}`, YYYYMMDD);
  }, [isAuthenticated, id, isMemberOfGroup]);
  
  if (isError || !id) {
    return <div className="container mx-auto px-4 py-8">
      <span>그룹 정보를 불러오는데 실패했습니다.</span>
      <button className='text-blue-900 flex items-center gap-1 underline  ' onClick={() => navigate('/groups', { replace: true })}>목록으로</button>
    </div>;
  }
  
  const handleOpenShareModal = () => {
    const url = window.location.href;
    showModal(
      <ShareModal 
        url={url}
        members={members as any[]}
        currentUserName={currentUser?.name}
        groupName={isLoading ? initialGroupName : group?.name}
        closeModal={closeModal}
        password={group?.password}
      />
    );
  };

  return (
    <div className="container mx-auto px-4 pt-16">
      <Header 
        title={""} 
        showBackButton 
        onBackClick={handleBackClick} 
        showRightButton={!isLoading && isMemberOfGroup}
        onRightButtonClick={() => {
          showModal(<GroupSettingsModal
              handleOpenShareModal={handleOpenShareModal}
              handleEditGroup={() => {
                navigate(`/groups/${id}/edit`);
              }}
              handleLeaveGroup={() => {
                leaveGroup(id);
              }}
            />)
        }} 
      />
      <div className="flex items-baseline relative mb-6">
        <h1 className="text-[26px] font-bold">
          {isLoading ? (initialGroupName || '...') : group?.name}
        </h1>
        <div className="flex items-center gap-1 ml-3 cursor-pointer" onClick={() => {
          if (!isLoading) {
            handleOpenShareModal();
          }
        }}>
          <img src={icons.person} alt="back" className="w-4 h-4" />
          <span className="text-gray-800 font-semibold">
            {isLoading ? (initialMemberCount || '..') : members?.length}명 
          </span>
        </div>
      </div>
      
      {/* 탭 메뉴와 내용 */}
      <GroupDetailTabs 
        groupId={id}
        currency={group?.default_currency || 'KRW'}
        currentUser={currentUser}
        currentUserMemberId={currentUserMemberId || ''}
        members={members || []}
        memberNameMap={memberNameMap}
        treasurerMemberId={group?.treasurer || null}
        isMemberOfGroup={isMemberOfGroup}
        openInviteModal={handleOpenShareModal}
        isLoading={isLoading}
      />

    </div>
  );
};

export default GroupDetailPage; 